/**
 * @file Error boundary component for catching and displaying errors
 * @version 1.0.0
 * @author Matthieu Chareton
 * @since 2025-07-08
 */

import { useErrorBoundary } from '../core/errors.js';

const { defineComponent, h, watch } = Vue;

export default defineComponent({
  name: 'ErrorBoundary',

  props: {
    fallback: {
      type: [Object, Function],
      default: null,
    },
    showDetails: {
      type: Boolean,
      default: false,
    },
    resetOnPropsChange: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['error', 'reset'],

  setup(props, { emit, slots }) {
    const { boundaryErrors, hasBoundaryErrors, clearBoundaryErrors } =
      useErrorBoundary();

    const reset = () => {
      clearBoundaryErrors();
      emit('reset');
    };

    // Watch for prop changes to reset error state
    if (props.resetOnPropsChange) {
      watch(
        () => props,
        () => {
          if (hasBoundaryErrors.value) {
            reset();
          }
        },
        { deep: true }
      );
    }

    return () => {
      // If there are boundary errors, show error UI
      if (hasBoundaryErrors.value) {
        const latestError =
          boundaryErrors.value[boundaryErrors.value.length - 1];

        // If custom fallback is provided, use it
        if (props.fallback) {
          if (typeof props.fallback === 'function') {
            return props.fallback({
              error: latestError,
              reset,
              hasErrors: hasBoundaryErrors.value,
            });
          } else {
            return h(props.fallback, {
              error: latestError,
              reset,
              hasErrors: hasBoundaryErrors.value,
            });
          }
        }

        // Default error UI
        return h(
          'div',
          {
            class: 'alert alert-danger',
            role: 'alert',
          },
          [
            h(
              'div',
              { class: 'd-flex justify-content-between align-items-center' },
              [
                h('h4', { class: 'alert-heading mb-0' }, [
                  h('i', { class: 'bi bi-exclamation-triangle-fill me-2' }),
                  'Something went wrong',
                ]),
                h(
                  'button',
                  {
                    type: 'button',
                    class: 'btn btn-sm btn-outline-danger',
                    onClick: reset,
                  },
                  [h('i', { class: 'bi bi-arrow-clockwise me-1' }), 'Try Again']
                ),
              ]
            ),

            h('div', { class: 'mt-3' }, [
              h(
                'p',
                { class: 'mb-2' },
                'An unexpected error occurred in this component. Your data is safe.'
              ),

              // Show error details if enabled
              props.showDetails &&
                h('details', { class: 'mt-3' }, [
                  h(
                    'summary',
                    { class: 'text-muted small' },
                    'Technical Details'
                  ),
                  h(
                    'pre',
                    {
                      class: 'text-muted small mt-2 p-2 bg-light rounded fs-6',
                    },
                    [
                      `Component: ${latestError.component}\n`,
                      `Time: ${latestError.timestamp.toLocaleString()}\n`,
                      `Error: ${latestError.error.message}\n`,
                      latestError.error.stack &&
                        `Stack: ${latestError.error.stack}`,
                    ]
                  ),
                ]),
            ]),
          ]
        );
      }

      // No errors, render children normally
      return slots.default?.();
    };
  },
});
